import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Edit, History, MessageSquare, Eye, Calendar, User, Loader2, Trash2, ThumbsUp, Clock, MoreHorizontal } from "lucide-react";
import { useArticle, useCategories, useDeleteArticle } from "@/hooks/useKBData";
import { useAuth } from "@/providers/AuthProvider";
import { formatDistanceToNow } from "date-fns";
import { renderArticleHtml } from "@/lib/renderArticle";
import { VersionHistoryDrawer } from "@/components/kb/VersionHistoryDrawer";


export function KBArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  // Fetch article data
  const { data: articleData, isLoading: articleLoading, error: articleError } = useArticle(slug || "");
  const { data: categoriesData } = useCategories();

  // Delete mutation
  const deleteMutation = useDeleteArticle();

  const article = articleData?.article;
  const categories = categoriesData?.categories || [];

  // Find the category for this article
  const category = categories.find(cat => cat.id === article?.category_id);

  // Render article HTML safely
  const articleHtml = article ? renderArticleHtml(article) : "";

  // Handle delete with confirmation
  const handleDelete = async () => {
    if (!article || !slug) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete "${article.title}"? This action cannot be undone.`
    );

    if (confirmed) {
      try {
        await deleteMutation.mutateAsync(slug);
        navigate("/app/kb");
      } catch (error) {
        console.error("Failed to delete article:", error);
        alert("Failed to delete article. Please try again.");
      }
    }
  };

  if (articleLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (articleError || !article) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
        <p className="text-muted-foreground mb-4">
          The article you're looking for doesn't exist or has been removed.
        </p>
        <Button onClick={() => navigate("/app/kb")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Knowledge Base
        </Button>
      </div>
    );
  }

  // Helper function to get user initials
  const getUserInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky Article Header */}
      <div className="sticky top-14 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/app/kb")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to articles</span>
          </button>

          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button variant="ghost" size="sm" className="gap-2 rounded-lg" asChild>
                <Link to={`/app/kb/${article.slug}/edit`}>
                  <Edit className="h-4 w-4" />
                  <span className="hidden sm:inline">Edit</span>
                </Link>
              </Button>
            )}
            <VersionHistoryDrawer
              articleSlug={article.slug}
              trigger={
                <Button variant="ghost" size="sm" className="gap-2 rounded-lg">
                  <History className="h-4 w-4" />
                  <span className="hidden sm:inline">History</span>
                </Button>
              }
            />
            <Button variant="ghost" size="icon" className="rounded-lg">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 rounded-lg text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Delete</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <header className="mb-8">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold">
            {category?.name || "Uncategorized"}
          </Badge>

          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4 leading-tight">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                  {getUserInitials(article.author_name || "Unknown")}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium">{article.author_name}</span>
            </div>
            <span className="text-border">•</span>
            <span>Updated {new Date(article.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
            <span className="text-border">•</span>
            <div className="flex items-center gap-1">
              <Eye className="h-3.5 w-3.5" />
              <span>{(article.views || 0).toLocaleString()} views</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="prose prose-neutral dark:prose-invert max-w-none">
          <div
            className="article-content prose-editor"
            dangerouslySetInnerHTML={{ __html: articleHtml }}
          />
        </div>

        {/* Feedback */}
        <div className="mt-12 p-6 rounded-2xl border border-border bg-muted/30">
          <p className="text-sm font-medium mb-3">Was this article helpful?</p>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" className="gap-2 rounded-lg">
              <ThumbsUp className="h-4 w-4" />
              Yes, thanks!
            </Button>
            <Button variant="ghost" size="sm" className="rounded-lg text-muted-foreground">
              Not really
            </Button>
          </div>
        </div>

        <Separator className="my-10" />

        {/* Comments Section */}
        <section className="mt-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Comments (0)
            </h2>
          </div>

          {/* Add Comment */}
          <div className="mb-6 p-4 rounded-xl border border-border bg-card">
            <div className="flex items-start gap-3">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs bg-primary/10 text-primary">
                  {getUserInitials(article.author_name || "U")}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <textarea
                  placeholder="Add a comment... Use @mention to tag someone"
                  className="w-full min-h-[80px] resize-none text-sm bg-transparent border-none outline-none placeholder:text-muted-foreground"
                  disabled
                />
                <div className="flex justify-end mt-2">
                  <Button size="sm" className="rounded-lg" disabled>
                    Post Comment
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Comments placeholder */}
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No comments yet. Be the first to share your thoughts!</p>
            <p className="text-xs mt-1">Comments functionality will be available in Phase 4.</p>
          </div>
        </section>
      </article>
    </div>
  );
}